import React, { useState, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCustomer } from '../contexts/CustomerContext';
import { Box, Stack, Flex, Heading, Text } from '@chakra-ui/react';
import { FiGrid, FiSettings, FiLogOut, FiChevronRight, FiChevronLeft, FiSun, FiMoon, FiCalendar, FiFileText, FiBriefcase, FiUserPlus, FiCheckSquare, FiUsers, FiBarChart2, FiClock, FiLogIn, FiBookOpen, FiZap, FiUser, FiMessageSquare } from 'react-icons/fi';
import RequireRole from './RequireRole';
import { ROLES, ROLE_NAMES } from '../constants/roles';
import { isFeatureEnabled } from '../constants/featureFlags';
import './Sidebar.css';

const NavItem = memo(({ icon: IconComponent, to, children, isExpanded, onClick, hoverBg, activeBg, textColor }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === to;

  const handleClick = (e) => {
    e.preventDefault();
    navigate(to);
    if (onClick) onClick();
  };

  return (
    <Box
      w="full"
      cursor="pointer"
      onClick={handleClick}
      p="8px"
      borderRadius="md"
      display="flex"
      alignItems="center"
      justifyContent={isExpanded ? 'flex-start' : 'center'}
      transition="all 0.3s ease"
      bg={isActive ? activeBg : 'transparent'}
      borderLeft={isActive ? '4px solid' : '4px solid transparent'}
      borderColor={isActive ? 'slate.600' : 'transparent'}
      _hover={{ bg: hoverBg }}
      color={textColor}
      title={!isExpanded ? children : undefined}
    >
      <Flex align="center" w="full">
        <Box as={IconComponent} fontSize="20px" minW="24px" />
        {isExpanded && (
          <Text ml="12px" whiteSpace="nowrap" opacity={isExpanded ? 1 : 0} transition="opacity 0.3s ease">
            {children}
          </Text>
        )}
      </Flex>
    </Box>
  );
});

const Sidebar = ({ onWidthChange }) => {
  const { logout, user, role } = useAuth();
  const { isDarkHeader, toggleHeaderTheme } = useTheme();
  const { customer } = useCustomer();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Color values based on custom theme
  const sidebarBg = isDarkHeader ? '#1e293b' : '#f1f5f9';
  const hoverBg = isDarkHeader ? '#334155' : '#e2e8f0';
  const borderColor = isDarkHeader ? '#475569' : '#cbd5e1';
  const textColor = isDarkHeader ? '#ffffff' : '#334155';
  const activeBg = isDarkHeader ? '#334155' : '#cbd5e1';

  const closeMobileSidebar = () => setIsMobileOpen(false);

  const toggleSidebar = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    // Notify parent component of width change
    if (onWidthChange) {
      onWidthChange(newExpandedState ? 200 : 70);
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.600"
          zIndex="98"
          display={{ base: 'block', md: 'none' }}
          onClick={closeMobileSidebar}
        />
      )}

      <Flex
        as="nav"
        direction="column"
        className={`sidebar-container ${isMobileOpen ? 'show' : ''}`}
        w={isExpanded ? '200px' : '70px'}
        h="calc(100vh - 48px)"
        bg={sidebarBg}
        color={textColor}
        py="8px"
        px="6px"
        transition="width 0.3s ease, background 0.3s ease, color 0.3s ease"
        position="fixed"
        left="0"
        top="48px"
        zIndex="99"
        boxShadow="2px 0 5px rgba(0,0,0,0.1)"
        overflow="hidden"
      >
        <Flex
          mb="12px"
          justifyContent="space-between"
          alignItems="center"
          h="32px"
          position="relative"
        >
          {isExpanded && (
            <Heading
              as="h1"
              fontSize="sm"
              noOfLines={1}
              whiteSpace="nowrap"
              opacity={isExpanded ? 1 : 0}
              transition="opacity 0.3s ease"
            >
              Collaige Portal
            </Heading>
          )}
          <Box
            as="button"
            onClick={toggleSidebar}
            p="4px"
            borderRadius="md"
            color={textColor}
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            position={isExpanded ? 'relative' : 'absolute'}
            right={isExpanded ? '0' : '50%'}
            transform={isExpanded ? 'none' : 'translateX(50%)'}
            _hover={{ bg: hoverBg }}
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box as={isExpanded ? FiChevronLeft : FiChevronRight} fontSize="18px" />
          </Box>
        </Flex>

        <Stack spacing="2px" align="stretch" flex="1">
          {/* Leads - CTD and above */}
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR]}>
            <NavItem to="/leads" icon={FiGrid} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Leads
            </NavItem>
          </RequireRole>

          {/* Families - Admins & Directors */}
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR]}>
            <NavItem to="/families" icon={FiUsers} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Families
            </NavItem>
          </RequireRole>

          {/* Calendar View - All Staff (Admins, Directors, Tutors) */}
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR, ROLES.TUTOR]}>
            <NavItem to="/scheduling-manager" icon={FiClock} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Class Scheduling
            </NavItem>
          </RequireRole>

          {/* Ultimate Scheduling - All Staff (Admins, Directors, Tutors) */}
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR, ROLES.TUTOR]}>
            <NavItem to="/ultimate-scheduling" icon={FiCalendar} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Calendar View
            </NavItem>
          </RequireRole>

          {/* Enrollment - Admins & Directors */}
          {/* HIDDEN per user request
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR]}>
            <NavItem to="/student-enrollment" icon={FiUserPlus} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Enrollment
            </NavItem>
          </RequireRole>
          */}

          {/* Legacy Class Scheduling - Admins & Directors */}
          {/* HIDDEN per user request
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR]}>
            <NavItem to="/class-scheduling" icon={FiCalendar} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Class Scheduling (Legacy)
            </NavItem>
          </RequireRole>
          */}

          {/* Check-In - Admins & Directors Only */}
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR]}>
            <NavItem to="/student-checkin" icon={FiLogIn} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Check-In
            </NavItem>
          </RequireRole>

          {/* Class Management - Tutors, CTD and above */}
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR, ROLES.TUTOR]}>
            <NavItem to="/class-management" icon={FiBookOpen} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Class Management
            </NavItem>
          </RequireRole>

          {/* Tutor Notes - HIDDEN per user request
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR, ROLES.TUTOR]}>
            <NavItem to="/tutor-notes" icon={FiFileText} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Tutor Notes
            </NavItem>
          </RequireRole>
          */}

          {/* Parent Portal - Parents only */}
          <RequireRole roles={[ROLES.PARENT]}>
            <NavItem to="/parent-portal" icon={FiUser} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Parent Portal
            </NavItem>
          </RequireRole>

           {/* Reports - CTD and above, plus Parents */}
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR, ROLES.PARENT]}>
            <NavItem to="/reports" icon={FiBarChart2} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Reports
            </NavItem>
          </RequireRole>

          {/* Messages */}
          {isFeatureEnabled('MESSAGES') && (
            <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR]}>
              <NavItem to="/messages" icon={FiMessageSquare} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
                Messages
              </NavItem>
            </RequireRole>
          )}

          {/* Messages - Parents */}
          {isFeatureEnabled('MESSAGES') && (
            <RequireRole roles={[ROLES.PARENT]}>
              <NavItem to="/parent-messages" icon={FiMessageSquare} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
                Messages
              </NavItem>
            </RequireRole>
          )}

          {/* Tutor Schedule - Parents */}
          <RequireRole roles={[ROLES.PARENT]}>
            <NavItem to="/tutor-schedule" icon={FiUser} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Tutor Schedule
            </NavItem>
          </RequireRole>

          {/* Administration - CTA, CTD and above */}
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR]}>
            <NavItem to="/administration" icon={FiBriefcase} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Administration
            </NavItem>
          </RequireRole>

          {/* Settings - CTA and above */}
          <RequireRole roles={[ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN]}>
            <NavItem to="/settings" icon={FiSettings} isExpanded={isExpanded} onClick={closeMobileSidebar} hoverBg={hoverBg} activeBg={activeBg} textColor={textColor}>
              Settings
            </NavItem>
          </RequireRole>
        </Stack>

        <Box mt="auto">
          <Stack spacing="2px" align="stretch" borderTopWidth={1} borderColor={borderColor} pt="8px">
            {/* User Info */}
            {user && (
              <Box
                display="flex"
                alignItems="center"
                w="full"
                p="8px"
                borderRadius="md"
                justifyContent={isExpanded ? 'flex-start' : 'center'}
                transition="all 0.3s ease"
                title={!isExpanded ? `${user.first_name} ${user.last_name}\n${ROLE_NAMES[role] || role}${customer?.name ? `\n${customer.name}` : ''}` : undefined}
              >
                <Flex align="center" w="full">
                  <Box as={FiUser} fontSize="20px" minW="24px" />
                  {isExpanded && (
                    <Box ml="12px" overflow="hidden">
                      <Text
                        fontSize="sm"
                        fontWeight="600"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        opacity={isExpanded ? 1 : 0}
                        transition="opacity 0.3s ease"
                      >
                        {user.first_name} {user.last_name}
                      </Text>
                      <Text
                        fontSize="xs"
                        opacity={0.7}
                        whiteSpace="nowrap"
                      >
                        {ROLE_NAMES[role] || role}
                      </Text>
                      {customer?.name && (
                        <Text
                          fontSize="xs"
                          opacity={0.6}
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          {customer.name}
                        </Text>
                      )}
                    </Box>
                  )}
                </Flex>
              </Box>
            )}

            <Box
              onClick={toggleHeaderTheme}
              display="flex"
              alignItems="center"
              w="full"
              p="8px"
              borderRadius="md"
              _hover={{ bg: hoverBg }}
              justifyContent={isExpanded ? 'flex-start' : 'center'}
              transition="all 0.3s ease"
              cursor="pointer"
              title={!isExpanded ? (isDarkHeader ? 'Light Theme' : 'Dark Theme') : undefined}
            >
              <Flex align="center" w="full">
                <Box as={isDarkHeader ? FiSun : FiMoon} fontSize="20px" minW="24px" />
                {isExpanded && (
                  <Text ml="12px" whiteSpace="nowrap" opacity={isExpanded ? 1 : 0} transition="opacity 0.3s ease">
                    {isDarkHeader ? 'Light Theme' : 'Dark Theme'}
                  </Text>
                )}
              </Flex>
            </Box>
            <Box
              onClick={logout}
              display="flex"
              alignItems="center"
              w="full"
              p="8px"
              borderRadius="md"
              _hover={{ bg: 'red.600' }}
              justifyContent={isExpanded ? 'flex-start' : 'center'}
              transition="all 0.3s ease"
              cursor="pointer"
              title={!isExpanded ? 'Logout' : undefined}
            >
              <Flex align="center" w="full">
                <Box as={FiLogOut} fontSize="20px" minW="24px" />
                {isExpanded && (
                  <Text ml="12px" whiteSpace="nowrap" opacity={isExpanded ? 1 : 0} transition="opacity 0.3s ease">
                    Logout
                  </Text>
                )}
              </Flex>
            </Box>
          </Stack>
        </Box>
      </Flex>
    </>
  );
};

// Memoize Sidebar to prevent re-renders on route changes
export default memo(Sidebar);
